/**
 * Creates a LineBasic Material, which can be extended with GL nodes.
 *
 * @remarks
 * This node can create children, which will be GL nodes. The GLSL code generated by the nodes will extend the Material.
 *
 */
import {NodeParamsConfig, ParamConfig} from '../utils/params/ParamsConfig';
import {UniformsTransparencyParamConfig, UniformsTransparencyController} from './utils/UniformsTransparencyController';
import {AdvancedCommonController, AdvancedCommonParamConfig} from './utils/AdvancedCommonController';
import {ShaderAssemblerLine} from '../gl/code/assemblers/materials/Line';
import {TypedBuilderMatNode, BaseBuilderParamConfig} from './_BaseBuilder';
import {AssemblerName} from '../../poly/registers/assemblers/_BaseRegister';
import {Poly} from '../../Poly';
import {DefaultFolderParamConfig} from './utils/DefaultFolder';
import {AdvancedFolderParamConfig} from './utils/AdvancedFolder';
import {LineBasicMaterial} from 'three';
import {CustomMaterialName} from '../../../core/geometry/Material';
import {Material} from 'three';
// import {
// 	CustomMaterialLineParamConfig,
// 	materialLineAssemblerCustomMaterialRequested,
// } from './utils/customMaterials/CustomMaterialLine';

interface LineBasicBuilderControllers {
	advancedCommon: AdvancedCommonController;
}
interface LineBasicBuilderMaterial extends LineBasicMaterial {
	vertexShader: string;
	fragmentShader: string;
	customMaterials: {
		[key in CustomMaterialName]?: Material;
	};
}

class LineBasicBuilderMatParamsConfig extends AdvancedCommonParamConfig(
	BaseBuilderParamConfig(
		/* advanced */ AdvancedFolderParamConfig(
			UniformsTransparencyParamConfig(DefaultFolderParamConfig(NodeParamsConfig))
		)
	)
) {
	linewidth = ParamConfig.FLOAT(1, {
		range: [0, 10],
		rangeLocked: [true, false],
	});
}
const ParamsConfig = new LineBasicBuilderMatParamsConfig();

export class LineBasicBuilderMatNode extends TypedBuilderMatNode<
	LineBasicBuilderMaterial,
	ShaderAssemblerLine,
	LineBasicBuilderMatParamsConfig
> {
	override paramsConfig = ParamsConfig;
	static override type() {
		return 'lineBasicBuilder';
	}
	public override usedAssembler(): Readonly<AssemblerName.GL_LINE> {
		return AssemblerName.GL_LINE;
	}
	protected _createAssemblerController() {
		return Poly.assemblersRegister.assembler(this, this.usedAssembler());
	}
	// public override customMaterialRequested(customName: CustomMaterialName): boolean {
	// 	return materialLineAssemblerCustomMaterialRequested(this, customName);
	// }
	readonly controllers: LineBasicBuilderControllers = {
		advancedCommon: new AdvancedCommonController(this),
	};
	private controllerNames = Object.keys(this.controllers) as Array<keyof LineBasicBuilderControllers>;

	override initializeNode() {
		this.params.onParamsCreated('init controllers', () => {
			for (let controllerName of this.controllerNames) {
				this.controllers[controllerName].initializeNode();
			}
		});
	}
	override async cook() {
		for (let controllerName of this.controllerNames) {
			this.controllers[controllerName].update();
		}

		UniformsTransparencyController.update(this);

		this.compileIfRequired();

		this.material.linewidth = this.pv.linewidth;

		this.setMaterial(this.material);
	}
}
